import React from 'react';
import type {
  UsageSummaryData,
  OrgUsageSummaryData,
} from '../../../../../api/pleri-usage';
import { formatNumber } from './constants';

import { useTranslation } from 'react-i18next';

export function UsageHero({
  summary,
}: {
  summary: UsageSummaryData | OrgUsageSummaryData | undefined;
  period: { start: string; end: string };
}) {
  const { t } = useTranslation('pleri/settings');
  if (!summary) {
    return (
      <p className="text-gray-500 dark:text-gray-400">
        {t('noUsageDataForThisPeriod')}
      </p>
    );
  }

  const limit = summary.tier.limit;
  const totalTokens = summary.tier.totalTokens;

  if (limit != null) {
    const usedPercent = limit.usedPercent ?? 0;
    const displayPercent = Math.min(usedPercent, 100);
    const isOver = limit.isOverLimit;
    const remaining = limit.remainingTokens ?? 0;

    return (
      <div className="w-full">
        <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-100 sm:h-3.5 dark:bg-gray-800">
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${displayPercent}%`,
              background: isOver
                ? 'linear-gradient(90deg, #f87171 0%, #dc2626 100%)'
                : 'linear-gradient(90deg, #fdba74 0%, #f97316 100%)',
              boxShadow: isOver
                ? '0 0 12px rgba(220, 38, 38, 0.35)'
                : '0 0 12px rgba(249, 115, 22, 0.35)',
            }}
          />
        </div>
        <div className="mt-3 flex flex-wrap items-baseline justify-between gap-4">
          <div>
            <p className="text-2xl leading-tight font-semibold tracking-tight text-gray-900 sm:text-3xl dark:text-gray-100">
              {isOver ? (
                <span className="text-red-600">
                  {t('overLimit', { amount: formatNumber(limit.overByTokens) })}
                </span>
              ) : (
                <>
                  <span>{formatNumber(remaining)}</span>
                  <span className="ml-1.5 text-lg leading-tight font-medium text-gray-500 sm:text-xl dark:text-gray-400">
                    {t('tokensLeft')}
                  </span>
                </>
              )}
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              {limit.limitTokens != null
                ? t('usedOfLimit', {
                    used: formatNumber(limit.usedTokens),
                    limit: formatNumber(limit.limitTokens),
                  })
                : t('amountUsed', { used: formatNumber(limit.usedTokens) })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl leading-tight font-semibold tracking-tight text-gray-900 tabular-nums sm:text-3xl dark:text-gray-100">
              {Math.round(usedPercent)}%
            </p>
            <p className="mt-0.5 text-xs font-medium text-gray-400 dark:text-gray-500">
              {t('used')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-2xl leading-tight font-semibold tracking-tight text-gray-900 sm:text-3xl dark:text-gray-100">
        {t('tokenCount', { amount: formatNumber(totalTokens) })}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
        {t('noLimitThisPeriod')}
      </p>
    </div>
  );
}
